import {
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Form,
  Image,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Popover,
  Row,
  Select,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Upload,
} from 'antd';
import ImgCrop from 'antd-img-crop';
import {
  ApiOutlined,
  ArrowLeftOutlined,
  CameraOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  ScanOutlined,
  DashboardOutlined,
  InboxOutlined,
  FieldTimeOutlined,
  TableOutlined,
  UserOutlined,
} from '@ant-design/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import BreadCrumb from '../modules/breadcrumbs';
import * as partnerApi from '../modules/partners/api';
import { getWebhookConfig } from '../modules/webhook-logs/api';
import Cookies from 'js-cookie';
import PartnerItemsModal from '../components/Modals/PartnerItemsModal';
import PartnerColumnDefaultsModal from '../components/Modals/PartnerColumnDefaultsModal';
import PartnerTours from './PartnerTours';
import PartnerTicketsTab from '../components/PartnerTicketsTab';
import PartnerTicketsPage from './PartnerTickets';
import PartnerSites from './PartnerSites';
import PartnerCollectionPoints from './PartnerCollectionPoints';
import PartnerSchedules from './PartnerSchedules';
import PartnerSitesReadOnly from './PartnerSitesReadOnly';
import PartnerSchedulesReadOnly from './PartnerSchedulesReadOnly';
import { impersonatePartnerViewer } from '../modules/auth/api';
import { startImpersonation } from '../utils/impersonation';
import { TOKEN_NAME } from '../constants';
import { ReactSVG } from 'react-svg';
import RoutesTable from '../components/Tables/RoutesTable';
import OrdersTable from '../components/Tables/OrdersTable';
import PartnerLogo from '../components/PartnerLogo';
import { uploadSharedFile } from '../modules/orders/api';
import OrdersFilter from '../components/Forms/Filters/OrdersFilter';
import ActiveOrderFiltersBar from '../components/Forms/Filters/ActiveOrderFiltersBar';
import RoutesFilter from '../components/Forms/Filters/RoutesFilter';
import ServiceTimeOverridesNote from '../components/ServiceTimeOverridesNote';
import { downloadRoutesCsv } from '../utils/routeCsvExport';
import { downloadOrdersCsv } from '../utils/orderCsvExport';
import { selectAllRoutes } from '../modules/routes/stores/slice';
import { selectAllOrders } from '../modules/orders/stores/slice';
import { doGetRoutes } from '../modules/routes/stores/thunk';
import { doGetOrders, doDeleteOrders } from '../modules/orders/stores/thunk';
import { selectAllPartnerOrders } from '../modules/partner-orders/stores/slice';
import { doGetPartnerOrders } from '../modules/partner-orders/stores/thunk';
import { DATE_BACKEND_FORMAT, ORDER_STATUSES, PAGE_SIZE } from '../constants';
import PartnerStats from './PartnerStats';
import './PartnerDetail.scss';

const { TextArea } = Input;

// Read the page size the operator saved for the Routes table (column-config
// drawer, persisted by useColumnConfig under this localStorage key). The
// `routeTableConfigRef` only populates a render AFTER the table mounts, so the
// FIRST routes fetch (on tab open) used to fall back to the small PAGE_SIZE
// default while the table displayed "100 / page" — the list looked truncated
// ("stops at May 7") until the operator re-applied the filter. Reading the
// saved size synchronously here keeps the initial fetch and the displayed page
// size in agreement. Falls back to PAGE_SIZE when nothing is saved.
const getSavedRoutesPageSize = () => {
  try {
    const raw = localStorage.getItem('ovo-table-config-routes-columns');
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.pageSize || PAGE_SIZE;
  } catch {
    return PAGE_SIZE;
  }
};

const CATEGORY_OPTIONS = [
  { label: 'Product', value: 'PRODUCT' },
  { label: 'Empty', value: 'EMPTY' },
];

const CATEGORY_COLORS = {
  PRODUCT: 'blue',
  EMPTY: 'orange',
};

// Webhook event types a partner can opt into. Empty selection = all events sent
// (default). Keep in sync with the backend WEBHOOK_EVENT_TYPES.
const WEBHOOK_EVENT_OPTIONS = [
  { label: 'Order dispatched', value: 'order.dispatched' },
  { label: 'Order updated', value: 'order.updated' },
  { label: 'Order arriving soon', value: 'order.arriving_soon' },
  { label: 'Route started', value: 'route.started' },
  { label: 'Route ended', value: 'route.ended' },
  { label: 'Step — pickup', value: 'step.pickup' },
  { label: 'Step — delivery', value: 'step.delivery' },
  { label: 'Step — service', value: 'step.service' },
  { label: 'Step — unchecked', value: 'step.unchecked' },
];

const PartnerDetailPage = ({ defaultTab, viewOnly }) => {
  const { t } = useTranslation(['partners', 'common', 'routes', 'orders']);
  const { id: paramId } = useParams();
  const authUser = useSelector((state) => state.auth.user);
  const host = authUser?.host;
  const id = viewOnly ? authUser?.partnerId : paramId;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(defaultTab || 'overview');
  useEffect(() => {
    if (defaultTab && defaultTab !== activeTab) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // The effective global default webhook rate, so the per-partner override
  // field can show what "Global default" actually resolves to. Fail-soft.
  // Admin-only setting: not on the partner portal (it answered 403 there).
  useEffect(() => {
    if (viewOnly) return;
    getWebhookConfig()
      .then(({ response }) => setWebhookDefaultRate(response?.defaultRatePerSecond ?? null))
      .catch(() => {});
  }, [viewOnly]);

  const [partnerForm] = Form.useForm();
  const [itemTypeForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const [partner, setPartner] = useState(null);
  const [webhookDefaultRate, setWebhookDefaultRate] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [itemTypes, setItemTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemTypesLoading, setItemTypesLoading] = useState(false);

  // Partner edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  // Item type modal
  const [itemTypeModalOpen, setItemTypeModalOpen] = useState(false);
  const [editingItemType, setEditingItemType] = useState(null);
  const [itemTypeSaving, setItemTypeSaving] = useState(false);
  const [manageItemsType, setManageItemsType] = useState(null);

  // Route history
  // Admin path uses the shared routes Redux store; partner-viewer path hits
  // the dedicated /partners/me/routes endpoint and lives in local state so
  // it never touches the (admin-shaped) routes slice that includes other
  // partners' fields. OVO-002 — keep the two data shapes strictly separate.
  const adminRoutes = useSelector(selectAllRoutes);
  const { metadata: adminRoutesMeta, isFetching: adminRoutesFetching } = useSelector((state) => state.route);
  const [viewOnlyRoutes, setViewOnlyRoutes] = useState([]);
  const [viewOnlyRoutesMeta, setViewOnlyRoutesMeta] = useState({ totalItems: 0, totalPages: 0, currentPage: 1 });
  const [viewOnlyRoutesFetching, setViewOnlyRoutesFetching] = useState(false);
  const routes = viewOnly ? viewOnlyRoutes : adminRoutes;
  const routesMetadata = viewOnly ? viewOnlyRoutesMeta : adminRoutesMeta;
  const routesFetching = viewOnly ? viewOnlyRoutesFetching : adminRoutesFetching;
  const [routesLoaded, setRoutesLoaded] = useState(false);
  const [routeSearchValue, setRouteSearchValue] = useState('');
  const [routesCurrentPage, setRoutesCurrentPage] = useState(1);
  const [routeFilterQuery, setRouteFilterQuery] = useState({});
  const [isRouteFilterOpen, setIsRouteFilterOpen] = useState(false);
  const [selectedRouteKeys, setSelectedRouteKeys] = useState([]);
  const [isRouteActionOpen, setIsRouteActionOpen] = useState(false);

  // Collected Items
  const [collectedItems, setCollectedItems] = useState([]);
  const [collectedItemsLoading, setCollectedItemsLoading] = useState(false);
  const [collectedItemsLoaded, setCollectedItemsLoaded] = useState(false);
  const [collectedItemsPagination, setCollectedItemsPagination] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
  });

  // Viewers
  const [viewers, setViewers] = useState([]);
  const [viewersLoading, setViewersLoading] = useState(false);
  const [viewersLoaded, setViewersLoaded] = useState(false);
  const [viewerModalOpen, setViewerModalOpen] = useState(false);
  const [editingViewer, setEditingViewer] = useState(null);
  const [viewerSaving, setViewerSaving] = useState(false);
  const [viewerForm] = Form.useForm();

  // Orders (use partner-orders store when viewOnly, regular orders store when admin)
  const adminOrders = useSelector(selectAllOrders);
  const partnerOrders = useSelector(selectAllPartnerOrders);
  const allOrders = viewOnly ? partnerOrders : adminOrders;
  const { metadata: adminOrdersMeta, isFetching: adminOrdersFetching } = useSelector((state) => state.order);
  const { metadata: partnerOrdersMeta, isFetching: partnerOrdersFetching } = useSelector((state) => state.partnerOrder);
  const ordersMetadata = viewOnly ? partnerOrdersMeta : adminOrdersMeta;
  const ordersFetching = viewOnly ? partnerOrdersFetching : adminOrdersFetching;
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [orderSearchValue, setOrderSearchValue] = useState('');
  const [orderFilterQuery, setOrderFilterQuery] = useState({});
  const [isOrderFilterOpen, setIsOrderFilterOpen] = useState(false);
  const [ordersCurrentPage, setOrdersCurrentPage] = useState(1);
  const [selectedOrderKeys, setSelectedOrderKeys] = useState([]);
  const [isOrderActionOpen, setIsOrderActionOpen] = useState(false);
  const orderTableConfigRef = useRef({});
  // OVO-002 — Routes tab in PartnerDetail wires the same column-config
  // pattern the Orders tab uses, so the top-right settings button works
  // and the bottom page-size dropdown isn't duplicated (drawer wins).
  const routeTableConfigRef = useRef({});

  // Webhook config
  const [webhookModalOpen, setWebhookModalOpen] = useState(false);
  const [columnDefaultsModalOpen, setColumnDefaultsModalOpen] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [webhookForm] = Form.useForm();

  const breadCrumbs = viewOnly
    ? [{ text: partner?.name || '...' }]
    : [{ text: 'Partners', url: '/admin/partners' }, { text: partner?.name || '...' }];

  const fetchPartner = useCallback(async () => {
    setLoading(true);
    const { response } = viewOnly ? await partnerApi.getMyPartner() : await partnerApi.getPartner(id);
    if (response) {
      setPartner(response);
    }
    setLoading(false);
  }, [id, viewOnly]);

  // OVO-125 — upload (already cropped by ImgCrop) a new partner logo, link it
  // to the partner via the shared-upload pipeline, then refresh.
  const handleLogoUpload = useCallback(
    async (file) => {
      setLogoUploading(true);
      try {
        const { response: uploaded } = await uploadSharedFile(file);
        if (!uploaded?.id) throw new Error('upload failed');
        const { response } = await partnerApi.updatePartner({ id, logoFileId: uploaded.id });
        if (response) {
          setPartner(response);
          messageApi.success(t('partners:logoUpdated', 'Logo updated'));
        }
      } catch (e) {
        messageApi.error(t('partners:logoUploadFailed', 'Could not upload the logo'));
      } finally {
        setLogoUploading(false);
      }
      return false; // prevent antd's default upload
    },
    [id, messageApi, t],
  );

  const handleLogoRemove = useCallback(async () => {
    setLogoUploading(true);
    try {
      const { response } = await partnerApi.updatePartner({ id, logoFileId: null });
      if (response) {
        setPartner(response);
        messageApi.success(t('partners:logoRemoved', 'Logo removed'));
      }
    } finally {
      setLogoUploading(false);
    }
  }, [id, messageApi, t]);

  const fetchItemTypes = useCallback(async () => {
    setItemTypesLoading(true);
    const { response } = viewOnly ? await partnerApi.getMyPartnerItemTypes() : await partnerApi.getPartnerItemTypes(id);
    if (response) {
      setItemTypes(response);
    }
    setItemTypesLoading(false);
  }, [id, viewOnly]);

  const fetchPartnerRoutes = useCallback(
    async (params = {}) => {
      if (viewOnly) {
        // OVO-002 — partner-viewer path. Use the dedicated /partners/me/routes
        // endpoint, whose response is intentionally narrower than the admin
        // shape: no `partners[]` list, no `_count.orders` total, no
        // `collectedItemsByPartner[]`. Each row only carries the partner's
        // own counts (`partnerOrderCount`, `partnerCollectedItemsCount`),
        // so no other partner is reachable through this view.
        setViewOnlyRoutesFetching(true);
        const { response } = await partnerApi.getMyPartnerRoutes({
          page: 1,
          take: getSavedRoutesPageSize(),
          ...params,
        });
        if (response) {
          setViewOnlyRoutes(response.routes || []);
          setViewOnlyRoutesMeta(response.pagination || { totalItems: 0, totalPages: 0, currentPage: 1 });
        }
        setViewOnlyRoutesFetching(false);
      } else {
        dispatch(doGetRoutes({ partnerId: id, page: 1, take: getSavedRoutesPageSize(), ...params }));
      }
      setRoutesLoaded(true);
    },
    [id, dispatch, viewOnly],
  );

  const fetchPartnerOrders = useCallback(
    (params = {}) => {
      if (viewOnly) {
        dispatch(doGetPartnerOrders({ page: 1, take: PAGE_SIZE, ...params }));
      } else {
        dispatch(doGetOrders({ partnerId: id, page: 1, take: PAGE_SIZE, ...params }));
      }
      setOrdersLoaded(true);
    },
    [id, dispatch, viewOnly],
  );

  useEffect(() => {
    fetchPartner();
    fetchItemTypes();
  }, [fetchPartner, fetchItemTypes]);

  // --- Partner Edit ---
  const handleOpenEditModal = () => {
    partnerForm.setFieldsValue({
      name: partner?.name,
      code: partner?.code,
      contactEmail: partner?.contactEmail,
      contactPhone: partner?.contactPhone,
      address: partner?.address,
      notes: partner?.notes,
    });
    setEditModalOpen(true);
  };

  const handleSavePartner = async (values) => {
    setEditSaving(true);
    const { response } = await partnerApi.updatePartner({ id: Number(id), ...values });
    if (response) {
      setPartner(response);
      messageApi.success('Partner updated');
      setEditModalOpen(false);
    } else {
      messageApi.error('Failed to update partner');
    }
    setEditSaving(false);
  };

  // --- Item Type CRUD ---
  const handleOpenItemTypeModal = (itemType = null) => {
    setEditingItemType(itemType);
    if (itemType) {
      itemTypeForm.setFieldsValue({
        name: itemType.name,
        category: itemType.category || 'PRODUCT',
        codePrefix: itemType.codePrefix,
        unitOfMeasure: itemType.unitOfMeasure,
        requiresScan: itemType.requiresScan ?? false,
        requiresPhoto: itemType.requiresPhoto ?? false,
        requiresWeight: itemType.requiresWeight ?? false,
        requiresFill: itemType.requiresFill ?? false,
      });
    } else {
      itemTypeForm.resetFields();
      itemTypeForm.setFieldsValue({
        category: 'PRODUCT',
        requiresScan: false,
        requiresPhoto: false,
        requiresWeight: false,
        requiresFill: false,
      });
    }
    setItemTypeModalOpen(true);
  };

  const handleSaveItemType = async (values) => {
    setItemTypeSaving(true);
    if (editingItemType) {
      const { response } = await partnerApi.updatePartnerItemType({ id: editingItemType.id, ...values });
      if (response) {
        setItemTypes((prev) => prev.map((t) => (t.id === editingItemType.id ? response : t)));
        messageApi.success('Item type updated');
        setItemTypeModalOpen(false);
      } else {
        messageApi.error('Failed to update item type');
      }
    } else {
      const { response } = await partnerApi.createPartnerItemType(id, values);
      if (response) {
        setItemTypes((prev) => [...prev, response]);
        messageApi.success('Item type created');
        setItemTypeModalOpen(false);
      } else {
        messageApi.error('Failed to create item type');
      }
    }
    setItemTypeSaving(false);
  };

  const handleDeleteItemType = async (itemTypeId) => {
    const { response } = await partnerApi.removePartnerItemType(itemTypeId);
    if (response) {
      setItemTypes((prev) => prev.filter((t) => t.id !== itemTypeId));
      messageApi.success('Item type deleted');
    } else {
      messageApi.error('Failed to delete item type');
    }
  };

  // --- Collected Items ---
  const fetchCollectedItems = useCallback(
    async (page = 1) => {
      setCollectedItemsLoading(true);
      const params = { page, take: 20 };
      // OVO-151 — the viewer portal uses the /partners/me endpoint, which
      // scopes by the JWT's partnerId and drops the driver identity.
      const { response } = viewOnly
        ? await partnerApi.getMyPartnerCollectedItems(params)
        : await partnerApi.getPartnerCollectedItems(id, params);
      if (response) {
        setCollectedItems(response.items || []);
        setCollectedItemsPagination(response.pagination || {});
      }
      setCollectedItemsLoading(false);
      setCollectedItemsLoaded(true);
    },
    [id, viewOnly],
  );

  // --- Viewers ---
  const fetchViewers = useCallback(async () => {
    setViewersLoading(true);
    const { response } = viewOnly
      ? await partnerApi.getMyPartnerViewers()
      : await partnerApi.getPartnerViewers(id, { page: 1, take: 100 });
    if (response?.users) {
      setViewers(response.users);
    }
    setViewersLoading(false);
    setViewersLoaded(true);
  }, [id, viewOnly]);

  const handleOpenViewerModal = (viewer = null) => {
    setEditingViewer(viewer);
    if (viewer) {
      const nameParts = (viewer.name || '').split(' ');
      viewerForm.setFieldsValue({
        giveName: nameParts[0] || '',
        familyName: nameParts.slice(1).join(' ') || '',
        email: viewer.email,
        password: '',
        language: viewer.language || 'fr',
      });
    } else {
      viewerForm.resetFields();
    }
    setViewerModalOpen(true);
  };

  const handleSaveViewer = async (values) => {
    setViewerSaving(true);
    if (editingViewer) {
      const updateData = { ...values };
      if (!updateData.password) delete updateData.password;
      const { response } = await partnerApi.updatePartnerViewer(id, editingViewer.id, updateData);
      if (response) {
        fetchViewers();
        messageApi.success('Viewer updated');
        setViewerModalOpen(false);
      } else {
        messageApi.error('Failed to update viewer');
      }
    } else {
      const { response } = await partnerApi.createPartnerViewer(id, values);
      if (response) {
        fetchViewers();
        messageApi.success('Viewer created');
        setViewerModalOpen(false);
      } else {
        messageApi.error('Failed to create viewer');
      }
    }
    setViewerSaving(false);
  };

  const handleDeleteViewer = async (viewerId) => {
    const { response } = await partnerApi.removePartnerViewer(id, viewerId);
    if (response) {
      setViewers((prev) => prev.filter((v) => v.id !== viewerId));
      messageApi.success('Viewer deleted');
    } else {
      messageApi.error('Failed to delete viewer');
    }
  };

  // --- Impersonation (admin -> partner viewer) ---
  const handleImpersonate = async (partnerIdArg) => {
    if (impersonating) return;
    setImpersonating(true);
    try {
      const adminToken = Cookies.get(TOKEN_NAME);
      const { response, error } = await impersonatePartnerViewer(partnerIdArg);
      if (error || !response?.access_token) {
        messageApi.error(error?.message || 'Failed to impersonate');
        return;
      }
      // Stash admin token + the page to return to once we exit.
      startImpersonation({
        adminToken,
        impersonatedToken: response.access_token,
        returnPath: `/admin/partners/${partnerIdArg}`,
      });
      // Hard reload so Redux rebootstraps with the new token + the right
      // role/layout (admin -> partner-viewer flip touches enough slices that
      // a soft transition is fragile).
      window.location.assign('/partner/orders');
    } catch (e) {
      messageApi.error(e?.message || 'Failed to impersonate');
    } finally {
      setImpersonating(false);
    }
  };

  // --- Webhook Config ---
  const handleOpenWebhookModal = () => {
    webhookForm.setFieldsValue({
      webhookEnabled: partner?.webhookEnabled ?? false,
      webhookUrl: partner?.webhookUrl || '',
      webhookHeaders: partner?.webhookHeaders ? JSON.stringify(partner.webhookHeaders, null, 2) : '',
      webhookTransformer: partner?.webhookTransformer || null,
      webhookRateLimitPerSecond: partner?.webhookRateLimitPerSecond ?? null,
      webhookEvents: partner?.webhookEvents ?? [],
    });
    setWebhookModalOpen(true);
  };

  const handleSaveWebhook = async (values) => {
    // Parse headers JSON
    let headers = null;
    if (values.webhookHeaders && values.webhookHeaders.trim()) {
      try {
        headers = JSON.parse(values.webhookHeaders);
      } catch {
        messageApi.error('Invalid JSON for headers');
        return;
      }
    }
    setWebhookSaving(true);
    const { response } = await partnerApi.updatePartner({
      id: Number(id),
      webhookEnabled: values.webhookEnabled,
      webhookUrl: values.webhookUrl || null,
      webhookHeaders: headers,
      webhookTransformer: values.webhookTransformer || null,
      webhookRateLimitPerSecond:
        values.webhookRateLimitPerSecond === undefined || values.webhookRateLimitPerSecond === ''
          ? null
          : values.webhookRateLimitPerSecond,
      // Empty array = all events (default).
      webhookEvents: values.webhookEvents || [],
    });
    if (response) {
      setPartner(response);
      messageApi.success('Webhook configuration updated');
      setWebhookModalOpen(false);
    } else {
      messageApi.error('Failed to update webhook configuration');
    }
    setWebhookSaving(false);
  };

  // --- Tab change handler (lazy load route history / viewers) ---
  const handleTabChange = (key) => {
    setActiveTab(key);
    if (viewOnly) {
      navigate(key === 'overview' ? '/partner' : `/partner/${key}`, { replace: true });
    } else if (key === 'overview') {
      navigate(`/admin/partners/${id}`, { replace: true });
    } else {
      navigate(`/admin/partners/${id}/${key}`, { replace: true });
    }
    if (key === 'routes' && !routesLoaded) {
      fetchPartnerRoutes(routeFilterQuery);
    }
    if (key === 'orders' && !ordersLoaded) {
      fetchPartnerOrders();
    }
    if (key === 'collected' && !collectedItemsLoaded) {
      fetchCollectedItems();
    }
    if (key === 'viewers' && !viewersLoaded) {
      fetchViewers();
    }
  };

  // Auto-load routes/orders when landing on tabs via URL
  useEffect(() => {
    if (defaultTab === 'routes' && !routesLoaded) {
      fetchPartnerRoutes(routeFilterQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab, routesLoaded, fetchPartnerRoutes]);

  useEffect(() => {
    if (defaultTab !== 'orders') return;
    // OVO-101 — URL `?siteId=N` is the source of truth on this tab.
    // Fires on every Orders-tab navigation (the Sites tab "View orders"
    // cross-link, tab clicks, browser back/forward). Without this the
    // filter wouldn't update when navigating between Site rows after
    // the first Orders-tab visit.
    const search = new URLSearchParams(location.search);
    const siteIdFromUrl = search.get('siteId');
    const currentSiteId = String(orderFilterQuery.siteId || '');
    const desiredSiteId = String(siteIdFromUrl || '');

    if (currentSiteId !== desiredSiteId) {
      // URL diverges from the in-memory filter — re-sync. Build the new
      // filter by stripping the previous siteId and adding the new one
      // (if any). Other active filters (status, date range, etc.) are
      // preserved across the navigation.
      const next = { ...orderFilterQuery };
      delete next.siteId;
      if (siteIdFromUrl) next.siteId = siteIdFromUrl;
      next.current = 1;
      setOrderFilterQuery(next);
      fetchPartnerOrders(next);
    } else if (!ordersLoaded) {
      fetchPartnerOrders(orderFilterQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab, location.search]);

  useEffect(() => {
    if (defaultTab === 'collected' && !collectedItemsLoaded) {
      fetchCollectedItems();
    }
  }, [defaultTab, collectedItemsLoaded, fetchCollectedItems]);

  useEffect(() => {
    if (defaultTab === 'viewers' && !viewersLoaded) {
      fetchViewers();
    }
  }, [defaultTab, viewersLoaded, fetchViewers]);

  // --- Table columns ---
  const itemTypeColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (cat) => <Tag color={CATEGORY_COLORS[cat] || 'default'}>{cat || '-'}</Tag>,
    },
    {
      title: 'Code Prefix',
      dataIndex: 'codePrefix',
      key: 'codePrefix',
      render: (val) => val || '-',
    },
    {
      title: 'Unit',
      dataIndex: 'unitOfMeasure',
      key: 'unitOfMeasure',
      render: (val) => val || '-',
    },
    {
      title: 'Flags',
      key: 'flags',
      render: (_, record) => (
        <span className="item-type-flags">
          {record.requiresScan && (
            <Tag icon={<ScanOutlined />} color="processing">
              Scan
            </Tag>
          )}
          {record.requiresPhoto && (
            <Tag icon={<CameraOutlined />} color="processing">
              Photo
            </Tag>
          )}
          {record.requiresWeight && (
            <Tag icon={<DashboardOutlined />} color="processing">
              Weight
            </Tag>
          )}
          {record.requiresFill && (
            <Tag icon={<DashboardOutlined />} color="processing">
              Fill /10
            </Tag>
          )}
          {!record.requiresScan && !record.requiresPhoto && !record.requiresWeight && !record.requiresFill && '-'}
        </span>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (val) => (
        <Tag color={val !== false ? 'green' : 'default'} icon={val !== false ? <CheckCircleOutlined /> : null}>
          {val !== false ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Items',
      key: 'itemsCount',
      render: (_, record) => (record.category === 'EMPTY' ? record._count?.partnerItems ?? 0 : '-'),
    },
    !viewOnly && {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <span className="item-type-actions">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleOpenItemTypeModal(record)}>
            {t('common:edit')}
          </Button>
          {record.category === 'EMPTY' && (
            <Button type="link" size="small" onClick={() => setManageItemsType(record)}>
              {t('items.button')}
            </Button>
          )}
          <Popconfirm
            title={t('deleteItemType')}
            onConfirm={() => handleDeleteItemType(record.id)}
            okText={t('common:yes')}
            cancelText={t('common:no')}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              {t('common:delete')}
            </Button>
          </Popconfirm>
        </span>
      ),
    },
  ].filter(Boolean);

  const handleRouteTableChange = (paginationParams, _filters, sorter) => {
    setRoutesCurrentPage(paginationParams.current || 1);
    const params = {
      ...routeFilterQuery,
      partnerId: id,
      current: paginationParams.current,
      pageSize: paginationParams.pageSize,
      page: paginationParams.current,
      take: paginationParams.pageSize,
      q: routeSearchValue || undefined,
    };
    if (sorter && sorter.field && sorter.order) {
      params.sortField = sorter.field;
      params.sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
    }
    fetchPartnerRoutes(params);
  };

  const handleRouteSearch = () => {
    fetchPartnerRoutes({ ...routeFilterQuery, q: routeSearchValue || undefined });
  };

  const handleRouteFilter = (params) => {
    const newParams = { ...params };
    delete newParams.pageSizeOptions;
    delete newParams.partnerId;
    setRouteFilterQuery(newParams);
    setRoutesCurrentPage(newParams.current || 1);
    fetchPartnerRoutes({
      ...newParams,
      page: newParams.current || 1,
      take: Number(newParams.pageSize) || PAGE_SIZE,
    });
  };

  const handleExportPartnerRoutes = () => {
    if (isRouteActionOpen) setIsRouteActionOpen(false);
    const selected = routes.filter((r) => selectedRouteKeys?.includes(r.id));
    if (!selected.length) return;
    downloadRoutesCsv(selected);
    messageApi.success(t('routes:exportSuccess', { count: selected.length }));
  };

  const routeActiveFilterCount = [
    routeFilterQuery.dateFrom || routeFilterQuery.dateTo,
    routeFilterQuery.driver,
    routeFilterQuery.status,
  ].filter(Boolean).length;

  const routeWithAction = (record) => (
    <div className="actions">
      <Link to={`/admin/route-plans?date=${moment(record.date).format(DATE_BACKEND_FORMAT)}`}>
        <Button type="link" className="edit">
          <ReactSVG className="icon" src="/icons/route-plans.svg" />
        </Button>
      </Link>
    </div>
  );

  // --- Orders tab handlers ---
  const dispatchOrders = useCallback(
    (params) => {
      if (viewOnly) {
        dispatch(doGetPartnerOrders(params));
      } else {
        dispatch(doGetOrders({ partnerId: id, ...params }));
      }
    },
    [viewOnly, id, dispatch],
  );

  // AntD calls onChange as (pagination, filters, sorter) — this used to take
  // the pagination alone, so every column sorter (Delivery Date) was dropped on
  // the floor, on the admin Orders tab AND the partner-viewer /partner/orders
  // page (same component). Mirrors handleRouteTableChange above.
  const handleOrderTableChange = (params, _filters, sorter) => {
    const newParams = { ...orderFilterQuery, ...params };
    delete newParams.pageSizeOptions;

    const sortField = sorter?.field;
    const sortOrder = sorter?.order;
    if (sortField && sortOrder) {
      newParams.sortField = sortField;
      newParams.sortOrder = sortOrder === 'ascend' ? 'asc' : 'desc';
    } else if (sorter) {
      // Third click clears the sort — AntD still passes a sorter object.
      delete newParams.sortField;
      delete newParams.sortOrder;
    }
    // Keep the sort in the filter query so search / page-size changes (which
    // don't carry a sorter) don't silently reset it.
    setOrderFilterQuery((prev) => {
      const next = { ...prev };
      if (newParams.sortField) {
        next.sortField = newParams.sortField;
        next.sortOrder = newParams.sortOrder;
      } else {
        delete next.sortField;
        delete next.sortOrder;
      }
      return next;
    });

    const page = Number(newParams.current || 1);
    const take = Number(newParams.pageSize) || PAGE_SIZE;
    setOrdersCurrentPage(page);
    dispatchOrders({ page, take, ...newParams });
  };

  const handleOrderSearch = () => {
    const params = { ...orderFilterQuery, q: orderSearchValue || undefined, current: 1 };
    setOrdersCurrentPage(1);
    dispatchOrders({ page: 1, take: orderTableConfigRef.current?.pageSize || PAGE_SIZE, ...params });
  };

  const handleOrderFilter = (params) => {
    const newParams = { ...params };
    if (!viewOnly) newParams.partnerId = id;
    delete newParams.pageSizeOptions;
    setOrderFilterQuery(newParams);
    setOrdersCurrentPage(newParams.current || 1);
    dispatchOrders({
      page: newParams.current || 1,
      take: Number(newParams.pageSize) || orderTableConfigRef.current?.pageSize || PAGE_SIZE,
      ...newParams,
    });
  };

  const handleExportPartnerOrders = async () => {
    if (isOrderActionOpen) setIsOrderActionOpen(false);
    const selected = allOrders.filter((o) => selectedOrderKeys?.includes(o.orderCode));
    if (!selected.length) return;
    await downloadOrdersCsv(selected, `${partner?.code || 'partner'}-orders-export`);
    messageApi.success(`Exported ${selected.length} order${selected.length === 1 ? '' : 's'}`);
  };

  const DELETABLE_STATUSES = [
    ORDER_STATUSES.DRAFT,
    ORDER_STATUSES.GEOCODING,
    ORDER_STATUSES.ERROR,
    ORDER_STATUSES.UNASSIGNED,
  ];

  const handleDeleteOrder = async (record) => {
    try {
      await dispatch(doDeleteOrders({ orderCodes: [record.orderCode] })).unwrap();
      messageApi.success(`Order ${record.orderCode} deleted`);
      fetchPartnerOrders({ ...orderFilterQuery });
    } catch (error) {
      messageApi.error(error?.message || 'Failed to delete order');
    }
  };

  // Bulk-delete the currently-selected orders. Mirrors the same logic
  // as Orders.jsx (the global list): filters to deletable statuses,
  // refuses the batch if any selected order is in a non-deletable
  // state, otherwise dispatches one doDeleteOrders call. Closes the
  // Action popover and refreshes via the existing fetchPartnerOrders.
  const handleBulkDeleteOrders = async () => {
    const deletableCodes = allOrders
      .filter((o) => selectedOrderKeys?.includes(o.orderCode) && DELETABLE_STATUSES.includes(o.status))
      .map((o) => o.orderCode);

    if (deletableCodes.length !== selectedOrderKeys.length) {
      const blockedCodes = allOrders
        .filter((o) => selectedOrderKeys?.includes(o.orderCode) && !DELETABLE_STATUSES.includes(o.status))
        .map((o) => o.orderCode);
      messageApi.error({
        content: `Cannot delete: ${blockedCodes.join(', ')} (status not deletable)`,
        style: { marginTop: '52px' },
      });
      return;
    }
    try {
      await dispatch(doDeleteOrders({ orderCodes: selectedOrderKeys })).unwrap();
      messageApi.success(`Deleted ${selectedOrderKeys.length} order${selectedOrderKeys.length === 1 ? '' : 's'}`);
      setSelectedOrderKeys([]);
      setIsOrderActionOpen(false);
      fetchPartnerOrders({ ...orderFilterQuery });
    } catch (error) {
      messageApi.error(error?.message || 'Failed to delete orders');
    }
  };

  const orderWithAction = (record) => {
    if (viewOnly) {
      return (
        <div className="actions">
          <Link to={`/partner/orders/${record.orderCode}`}>
            <Button type="link" className="edit">
              <ReactSVG className="icon" src="/icons/edit.svg" />
            </Button>
          </Link>
        </div>
      );
    }
    const isDeletable = DELETABLE_STATUSES.includes(record.status);
    return (
      <div className="actions">
        <Link to={`/admin/orders/${record.orderCode}`}>
          <Button type="link" className="edit">
            <ReactSVG className="icon" src="/icons/edit.svg" />
          </Button>
        </Link>
        {isDeletable ? (
          <Popconfirm
            title={t('common:delete')}
            description={`Delete order ${record.orderCode}?`}
            onConfirm={() => handleDeleteOrder(record)}
            okText={t('common:delete')}
            cancelText={t('common:cancel')}
            okButtonProps={{ danger: true }}
          >
            <Button type="link" className="more">
              <ReactSVG className="icon" src="/icons/more.svg" />
            </Button>
          </Popconfirm>
        ) : (
          <Button type="link" className="more" disabled>
            <ReactSVG className="icon" src="/icons/more.svg" />
          </Button>
        )}
        {![ORDER_STATUSES.DRAFT, ORDER_STATUSES.GEOCODING, ORDER_STATUSES.ERROR].includes(record.status) && (
          <Link to={`/admin/route-plans?date=${moment(record.deliveryDate).format(DATE_BACKEND_FORMAT)}`}>
            <Button type="link" className="more">
              <ReactSVG className="icon" src="/icons/route-plans.svg" />
            </Button>
          </Link>
        )}
      </div>
    );
  };

  const orderActiveFilterCount = [
    orderFilterQuery.name,
    orderFilterQuery.pickupName,
    orderFilterQuery.routeId,
    orderFilterQuery.status,
    orderFilterQuery.deliveryDateFrom || orderFilterQuery.deliveryDateTo,
    orderFilterQuery.orderType,
    orderFilterQuery.issueTypes,
    // OVO-101 — Site filter contributes to the badge so the operator
    // sees it's active (especially relevant when arriving from the
    // Sites tab cross-link, where the filter is set from the URL).
    orderFilterQuery.siteId,
    // OVO-019 — Product Type filter (was missing from the count too).
    orderFilterQuery.partnerItemTypeIds,
  ].filter(Boolean).length;

  const viewerColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (val) => val || '-',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (val) => (val ? moment(val).format('DD/MM/YYYY HH:mm') : '-'),
    },
    !viewOnly && {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <span>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleOpenViewerModal(record)}>
            {t('common:edit')}
          </Button>
          <Popconfirm
            title={t('deleteViewer')}
            onConfirm={() => handleDeleteViewer(record.id)}
            okText={t('common:yes')}
            cancelText={t('common:no')}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              {t('common:delete')}
            </Button>
          </Popconfirm>
        </span>
      ),
    },
  ].filter(Boolean);

  const tabItems = [
    {
      key: 'overview',
      label: t('partners:overview'),
      children: (
        <>
          {/* Partner Info Card */}
          <Card
            title={t('partners:partnerDetails')}
            extra={
              !viewOnly && (
                <Button type="primary" icon={<EditOutlined />} onClick={handleOpenEditModal}>
                  {t('common:edit', { defaultValue: 'Edit' })}
                </Button>
              )
            }
            className="partner-info-card"
          >
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
              <Descriptions.Item label={t('common:name')}>{partner?.name || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('partners:code', { defaultValue: 'Code' })}>
                {partner?.code || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('partners:contactEmail')}>{partner?.contactEmail || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('partners:contactPhone')}>{partner?.contactPhone || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('partners:address')}>{partner?.address || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('partners:notes')} span={2}>
                {partner?.notes || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* OVO-125 — Partner Logo */}
          {!viewOnly && (
            <Card title={t('partners:logo', 'Logo')} className="partner-logo-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                {partner?.logoFile?.key ? (
                  <Image
                    src={`${host}/${partner.logoFile.key}`}
                    alt={partner?.name}
                    width={96}
                    height={96}
                    style={{ objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }}
                  />
                ) : (
                  <PartnerLogo name={partner?.name} size={96} style={{ borderRadius: 8 }} />
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: 13, maxWidth: 360 }}>
                    {t(
                      'partners:logoHint',
                      'Square image shown next to this partner across the app and on shared tracking links.',
                    )}
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <ImgCrop rotationSlider aspect={1} modalTitle={t('partners:cropLogo', 'Crop logo')}>
                      <Upload accept="image/*" showUploadList={false} beforeUpload={handleLogoUpload}>
                        <Button icon={<CameraOutlined />} loading={logoUploading}>
                          {partner?.logoFile?.key
                            ? t('partners:replaceLogo', 'Replace logo')
                            : t('partners:uploadLogo', 'Upload logo')}
                        </Button>
                      </Upload>
                    </ImgCrop>
                    {partner?.logoFile?.key && (
                      <Popconfirm
                        title={t('partners:removeLogoConfirm', 'Remove the logo?')}
                        onConfirm={handleLogoRemove}
                        okText={t('common:yes')}
                        cancelText={t('common:no')}
                      >
                        <Button danger icon={<DeleteOutlined />} loading={logoUploading}>
                          {t('common:remove', 'Remove')}
                        </Button>
                      </Popconfirm>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Item Types Section */}
          <Card
            title={t('partners:itemTypes')}
            extra={
              !viewOnly && (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenItemTypeModal(null)}>
                  {t('partners:addItemType', 'Add Item Type')}
                </Button>
              )
            }
            className="item-types-card"
          >
            <Table
              dataSource={itemTypes}
              columns={itemTypeColumns}
              rowKey="id"
              loading={itemTypesLoading}
              pagination={false}
              locale={{ emptyText: 'No item types defined yet' }}
              size="small"
            />
          </Card>

          {/* Webhook Configuration */}
          <Card
            title={
              <span>
                <ApiOutlined /> {t('partners:webhookConfig')}
              </span>
            }
            extra={
              !viewOnly && (
                <Button type="primary" icon={<EditOutlined />} onClick={handleOpenWebhookModal}>
                  Configure
                </Button>
              )
            }
            className="webhook-config-card"
          >
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
              <Descriptions.Item label="Status">
                <Tag color={partner?.webhookEnabled ? 'green' : 'default'}>
                  {partner?.webhookEnabled ? 'Active' : 'Inactive'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="URL">{partner?.webhookUrl || '-'}</Descriptions.Item>
              <Descriptions.Item label="Transformer">
                {partner?.webhookTransformer ? (
                  <Tag color="blue">{partner.webhookTransformer.toUpperCase()}</Tag>
                ) : (
                  'Standard'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Rate limit">
                {partner?.webhookRateLimitPerSecond
                  ? `${partner.webhookRateLimitPerSecond} req/s`
                  : `Global default${webhookDefaultRate != null ? ` (${webhookDefaultRate} req/s)` : ''}`}
              </Descriptions.Item>
              <Descriptions.Item label="Events">
                {partner?.webhookEvents?.length ? (
                  <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {partner.webhookEvents.map((e) => (
                      <Tag key={e} color="geekblue">
                        {e}
                      </Tag>
                    ))}
                  </span>
                ) : (
                  'All events'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Custom Headers">
                {partner?.webhookHeaders ? (
                  <pre className="webhook-headers-preview">{JSON.stringify(partner.webhookHeaders, null, 2)}</pre>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Arriving Soon">
                <Switch
                  checked={partner?.arrivingSoonEnabled}
                  disabled={viewOnly}
                  size="small"
                  onChange={async (checked) => {
                    const { response } = await partnerApi.updatePartner({
                      id: Number(id),
                      arrivingSoonEnabled: checked,
                    });
                    if (response) {
                      setPartner(response);
                      messageApi.success(checked ? 'Arriving Soon enabled' : 'Arriving Soon disabled');
                    }
                  }}
                />
                <span style={{ marginLeft: 8, fontSize: 12, color: '#9e9e9e' }}>
                  Notify next delivery as "Arriving soon" in Airtable
                </span>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* OVO-266 — per-partner service times feeding the ETA planning. */}
          {!viewOnly && (
            <Card
              title={
                <span>
                  <FieldTimeOutlined /> {t('partners:serviceTimes.title')}
                </span>
              }
              className="service-times-card"
            >
              <div style={{ color: 'var(--text-muted)', marginBottom: 12 }}>{t('partners:serviceTimes.hint')}</div>
              <ServiceTimeOverridesNote partnerId={partner?.id} />
              <Form
                layout="inline"
                initialValues={{
                  stopPickupMins: partner?.stopPickupMins,
                  taskPickupMins: partner?.taskPickupMins,
                  stopDeliveryMins: partner?.stopDeliveryMins,
                  taskDeliveryMins: partner?.taskDeliveryMins,
                }}
                key={`st-${partner?.id}-${partner?.updatedAt}`}
                onFinish={async (values) => {
                  const payload = {};
                  ['stopPickupMins', 'taskPickupMins', 'stopDeliveryMins', 'taskDeliveryMins'].forEach((k) => {
                    payload[k] = values[k] == null || values[k] === '' ? null : Number(values[k]);
                  });
                  await partnerApi.updatePartner({ id: partner.id, ...payload });
                  message.success(t('partners:serviceTimes.saved'));
                  fetchPartner();
                }}
              >
                <Form.Item
                  label={t('partners:serviceTimes.stopPickup')}
                  name="stopPickupMins"
                  tooltip={t('partners:serviceTimes.stopTooltip')}
                >
                  <InputNumber min={0} step={0.5} placeholder="3" addonAfter="min" style={{ width: 120 }} />
                </Form.Item>
                <Form.Item
                  label={t('partners:serviceTimes.taskPickup')}
                  name="taskPickupMins"
                  tooltip={t('partners:serviceTimes.taskTooltip')}
                >
                  <InputNumber min={0} step={0.5} placeholder="1" addonAfter="min" style={{ width: 120 }} />
                </Form.Item>
                <Form.Item
                  label={t('partners:serviceTimes.stopDelivery')}
                  name="stopDeliveryMins"
                  tooltip={t('partners:serviceTimes.stopTooltip')}
                >
                  <InputNumber min={0} step={0.5} placeholder="3" addonAfter="min" style={{ width: 120 }} />
                </Form.Item>
                <Form.Item
                  label={t('partners:serviceTimes.taskDelivery')}
                  name="taskDeliveryMins"
                  tooltip={t('partners:serviceTimes.taskTooltip')}
                >
                  <InputNumber min={0} step={0.5} placeholder="1.5" addonAfter="min" style={{ width: 120 }} />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit">
                    {t('partners:serviceTimes.save')}
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          )}

          {/* OVO-110 — default column layout for partner viewers */}
          {!viewOnly && (
            <Card
              title={
                <span>
                  <TableOutlined /> {t('partners:defaultColumns', 'Default columns for partner viewers')}
                </span>
              }
              extra={
                <Button type="primary" icon={<EditOutlined />} onClick={() => setColumnDefaultsModalOpen(true)}>
                  Configure
                </Button>
              }
              className="default-columns-card"
            >
              <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
                <Descriptions.Item label="Orders table">
                  {partner?.defaultColumns?.orders ? (
                    <Tag color="green">
                      Configured
                      {partner.defaultColumns.orders.hidden?.length > 0 &&
                        ` · ${partner.defaultColumns.orders.hidden.length} hidden`}
                    </Tag>
                  ) : (
                    <Tag>Global default</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Routes table">
                  {partner?.defaultColumns?.routes ? (
                    <Tag color="green">
                      Configured
                      {partner.defaultColumns.routes.hidden?.length > 0 &&
                        ` · ${partner.defaultColumns.routes.hidden.length} hidden`}
                    </Tag>
                  ) : (
                    <Tag>Global default</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="What this controls" span={2}>
                  <span style={{ fontSize: 12, color: '#9e9e9e' }}>
                    Sets the initial column visibility + order partner viewers see when they open their orders or routes
                    table for the first time. They can still override per-browser via the gear icon — "Reset to
                    defaults" falls back to whatever is configured here.
                  </span>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}
        </>
      ),
    },
    {
      key: 'stats',
      label: t('partners:statsTab'),
      children: <PartnerStats partnerId={id} viewOnly={viewOnly} partnerName={partner?.name} />,
    },
    {
      key: 'routes',
      label: t('partners:routesTab'),
      children: (
        <div className="route-history-section">
          <div className="page-header-table" style={{ marginBottom: 16 }}>
            <div className="search">
              <Input
                onChange={(e) => setRouteSearchValue(e.target.value)}
                onBlur={handleRouteSearch}
                onKeyDown={(e) => e.key === 'Enter' && handleRouteSearch()}
                placeholder={t('routes:searchRoutes', { defaultValue: 'Search routes' })}
                suffix={<img src="/icons/search.svg" alt="" />}
                value={routeSearchValue}
              />
            </div>
            <div className="buttons">
              {selectedRouteKeys.length > 0 && (
                <Popover
                  open={selectedRouteKeys?.length > 0 && isRouteActionOpen}
                  onOpenChange={(open) => setIsRouteActionOpen(open)}
                  trigger="click"
                  placement="bottom"
                  overlayClassName="OrdersPagePopoverAction"
                  content={
                    <div className="buttons">
                      <Button type="link" onClick={handleExportPartnerRoutes}>
                        {t('routes:exportCsv')}
                      </Button>
                    </div>
                  }
                >
                  <Button className="FilterButton ButtonFilter">
                    <div className="count">{selectedRouteKeys.length}</div>
                    {t('common:action')} <ReactSVG className="icon" src="/icons/arrow_drop_down.svg" />
                  </Button>
                </Popover>
              )}
              <Badge count={routeActiveFilterCount} size="small" offset={[-5, 0]}>
                <Button className="FilterButton ButtonFilter" onClick={() => setIsRouteFilterOpen(true)}>
                  {t('common:filter')}
                  <ReactSVG className="icon" src="/icons/filter_list.svg" />
                </Button>
              </Badge>
              {/* OVO-002 — column-config settings, same UX as the Orders tab. */}
              <Button
                type="text"
                onClick={() => routeTableConfigRef.current?.openDrawer?.()}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <ReactSVG className="icon" src="/icons/settings.svg" />
              </Button>
            </div>
          </div>
          <RoutesFilter
            open={isRouteFilterOpen}
            onClose={() => setIsRouteFilterOpen(false)}
            query={routeFilterQuery}
            onApply={handleRouteFilter}
            hidePartner
            hideDriver={viewOnly}
          />
          <RoutesTable
            dataSource={routes}
            rowKey="id"
            isFetching={routesFetching}
            withAction={viewOnly ? undefined : routeWithAction}
            partnerId={id}
            viewOnly={viewOnly}
            selectedRowKeys={selectedRouteKeys}
            onSelectChange={setSelectedRouteKeys}
            configRef={routeTableConfigRef}
            partnerDefaults={viewOnly ? partner?.defaultColumns?.routes : undefined}
            pagination={{
              current: routesCurrentPage,
              // Drawer-driven page size. Until the ref is populated on the first
              // render, fall back to the saved size (same value the first fetch
              // used) rather than PAGE_SIZE, so the table doesn't claim "100 /
              // page" while only PAGE_SIZE rows were actually fetched.
              pageSize: routeTableConfigRef.current?.pageSize || getSavedRoutesPageSize(),
              total: routesMetadata.totalItems || 0,
              // Bottom dropdown was broken — the drawer is the single
              // source of truth now.
              showSizeChanger: false,
              showTotal: (total) => `${total} Routes`,
            }}
            onChange={handleRouteTableChange}
            onPageSizeChange={(size) => {
              setRoutesCurrentPage(1);
              fetchPartnerRoutes({
                ...routeFilterQuery,
                page: 1,
                take: size,
                q: routeSearchValue || undefined,
              });
            }}
          />
        </div>
      ),
    },
    {
      key: 'orders',
      label: t('partners:ordersTab'),
      children: (
        <div className="partner-orders-section">
          <div className="page-header-table" style={{ marginBottom: 16 }}>
            <div className="search">
              <Input
                onChange={(e) => setOrderSearchValue(e.target.value)}
                onBlur={handleOrderSearch}
                onKeyDown={(e) => e.key === 'Enter' && handleOrderSearch()}
                placeholder={t('orders:searchOrders', { defaultValue: 'Search orders' })}
                suffix={<img src="/icons/search.svg" alt="" />}
                value={orderSearchValue}
              />
            </div>
            <div className="buttons">
              {selectedOrderKeys.length > 0 && (
                <Popover
                  open={selectedOrderKeys?.length > 0 && isOrderActionOpen}
                  onOpenChange={(open) => setIsOrderActionOpen(open)}
                  trigger="click"
                  placement="bottom"
                  overlayClassName="OrdersPagePopoverAction"
                  content={
                    <div className="buttons">
                      <Button type="link" onClick={handleExportPartnerOrders}>
                        {t('orders:exportCsv')}
                      </Button>
                      {/* Mirror the bulk-delete action from /admin/orders.
                          Service-side check guards against non-deletable
                          statuses, but we also filter client-side so the
                          confirmation only shows when something is actually
                          deletable. Hidden for partner-viewer mode. */}
                      {!viewOnly && (
                        <Popconfirm
                          title={t('common:areYouSureDelete', { defaultValue: 'Delete selected orders?' })}
                          onConfirm={handleBulkDeleteOrders}
                          okText={t('common:yes', { defaultValue: 'Yes' })}
                          cancelText={t('common:no', { defaultValue: 'No' })}
                          okButtonProps={{ danger: true }}
                        >
                          <Button type="link" danger>
                            {t('common:delete', { defaultValue: 'Delete' })}
                          </Button>
                        </Popconfirm>
                      )}
                    </div>
                  }
                >
                  <Button className="FilterButton ButtonFilter">
                    <div className="count">{selectedOrderKeys.length}</div>
                    {t('common:action')} <ReactSVG className="icon" src="/icons/arrow_drop_down.svg" />
                  </Button>
                </Popover>
              )}
              <Badge count={orderActiveFilterCount} size="small" offset={[-5, 0]}>
                <Button className="FilterButton ButtonFilter" onClick={() => setIsOrderFilterOpen(true)}>
                  Filter
                  <ReactSVG className="icon" src="/icons/filter_list.svg" />
                </Button>
              </Badge>
              <Button
                type="text"
                onClick={() => orderTableConfigRef.current?.openDrawer?.()}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <ReactSVG className="icon" src="/icons/settings.svg" />
              </Button>
            </div>
          </div>
          <OrdersFilter
            open={isOrderFilterOpen}
            onClose={() => setIsOrderFilterOpen(false)}
            query={orderFilterQuery}
            onApply={handleOrderFilter}
            hidePartner
            hideDriver={viewOnly}
            fixedPartnerId={id}
            viewOnly={viewOnly}
          />
          {/* OVO-101 — surface every active filter as a removable chip
              above the orders table. Drives the same handleOrderFilter
              the drawer uses, so removing a chip re-fetches the table. */}
          <ActiveOrderFiltersBar
            query={orderFilterQuery}
            onChange={(next) => handleOrderFilter({ ...next, partnerId: viewOnly ? undefined : id })}
            partnerId={id}
            viewOnly={viewOnly}
          />
          <div className="ContentMain">
            <OrdersTable
              dataSource={allOrders}
              pagination={{
                current: ordersCurrentPage,
                pageSize: orderTableConfigRef.current?.pageSize || PAGE_SIZE,
                total: ordersMetadata.totalItems || 0,
                showSizeChanger: false,
                showTotal: (total) => `${total} Orders`,
              }}
              withAction={orderWithAction}
              rowKey="orderCode"
              isFetching={ordersFetching}
              selectedRowKeys={selectedOrderKeys}
              onSelectChange={setSelectedOrderKeys}
              onChange={handleOrderTableChange}
              configRef={orderTableConfigRef}
              linkPrefix={viewOnly ? '/partner/orders' : '/admin/orders'}
              partnerDefaults={viewOnly ? partner?.defaultColumns?.orders : undefined}
              onPageSizeChange={(size) => {
                setOrdersCurrentPage(1);
                dispatchOrders({ page: 1, take: size, ...orderFilterQuery });
              }}
            />
          </div>
        </div>
      ),
    },
    {
      // OVO-151 — the Collected Empties tab is now ALSO shown to partner
      // viewers (the customer retired the legacy consigne-returns tool, so
      // the partner needs this list). The viewer path is isolation-safe:
      // data comes from /partners/me/collected-items (scoped by the JWT's
      // partnerId, EMPTY-category only, no driver identity) and the route
      // link resolves to the partner-scoped /partner/routes/:id. The
      // OVO-002 concern (cross-partner empties on mutualised routes) does
      // not apply — rows are filtered by the partner's own partnerId.
      key: 'collected',
      label: (
        <span>
          <InboxOutlined /> {t('partners:collectedEmpties', 'Collected Empties')}
        </span>
      ),
      children: (
        <div className="collected-empties-section">
          <div
            className="page-header-table"
            style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}
          >
            <Input.Search placeholder="Search collected items..." allowClear style={{ width: 300 }} disabled />
          </div>
          <Table
            dataSource={collectedItems}
            rowKey="id"
            loading={collectedItemsLoading}
            size="small"
            locale={{ emptyText: 'No collected items' }}
            pagination={{
              current: collectedItemsPagination.currentPage || 1,
              pageSize: 20,
              total: collectedItemsPagination.totalItems || 0,
              showTotal: (total) => `${total} items`,
            }}
            onChange={(pag) => fetchCollectedItems(pag.current)}
            columns={[
              {
                title: 'Date',
                dataIndex: 'collectedAt',
                key: 'collectedAt',
                width: 140,
                render: (val) => (val ? moment(val).format('DD/MM/YYYY HH:mm') : '-'),
              },
              {
                title: 'Route',
                dataIndex: ['route', 'id'],
                key: 'routeId',
                width: 80,
                render: (val) =>
                  val ? <Link to={viewOnly ? `/partner/routes/${val}` : `/admin/routes/${val}`}>{val}</Link> : '-',
              },
              {
                title: 'Order Ref',
                key: 'orderRef',
                width: 140,
                render: (_, record) => record.order?.partnerOrderRef || record.order?.orderCode || '-',
              },
              {
                title: 'Item Type',
                key: 'itemType',
                width: 160,
                render: (_, record) => {
                  const name = record.partnerItemType?.name || record.description || '-';
                  const cat = record.partnerItemType?.category;
                  return (
                    <span>
                      {name}
                      {cat && (
                        <Tag color={cat === 'EMPTY' ? 'orange' : 'blue'} style={{ marginLeft: 6, fontSize: 10 }}>
                          {cat}
                        </Tag>
                      )}
                    </span>
                  );
                },
              },
              {
                title: 'Qty',
                dataIndex: 'quantity',
                key: 'quantity',
                width: 60,
                align: 'center',
              },
              {
                title: 'Weight',
                key: 'weight',
                width: 100,
                render: (_, record) => {
                  if (!record.weight) return '-';
                  const unit = record.partnerItemType?.unitOfMeasure || '';
                  return `${record.weight} ${unit}`.trim();
                },
              },
              {
                title: 'Barcode',
                dataIndex: 'scannedCode',
                key: 'scannedCode',
                width: 140,
                render: (val) => val || '-',
              },
              // OVO-151 — the driver column is operator-side info; the
              // viewer endpoint doesn't even return collectedByUser.
              ...(viewOnly
                ? []
                : [
                    {
                      title: 'Driver',
                      key: 'driver',
                      width: 120,
                      render: (_, record) => record.collectedByUser?.name || '-',
                    },
                  ]),
            ]}
          />
        </div>
      ),
    },
    {
      // OVO-120 — partner-scoped tour mapping (admin only).
      key: 'tours',
      _hideForViewer: true,
      label: 'Tours',
      // OVO-120 — PartnerTours owns its strategy + rules + tours state
      // locally; no parent re-fetch needed for v1. If we later add cross-
      // tab dependencies (e.g. "show tour ref on Orders tab"), we'll add
      // an onPartnerUpdated callback that refreshes the parent.
      children: <PartnerTours partner={partner} />,
    },
    {
      // OVO-101 — Address Book (partner-scoped Sites). Admins get the full
      // CRUD + Discover catalog; partner viewers get a read-only table fed
      // by the JWT-scoped /partners/me/sites endpoint (the admin component
      // hits /partners/:id/sites, which is ADMIN-only and would 403).
      key: 'sites',
      label: 'Sites',
      children: viewOnly ? <PartnerSitesReadOnly /> : <PartnerSites partner={partner} />,
    },
    {
      // OVO-206 — collection points ("Caisses") at this partner's Sites.
      // PartnerCollectionPoints is viewOnly-aware (read-only via the
      // JWT-scoped /partners/me/collection-points endpoint for viewers).
      key: 'collection-points',
      label: 'Collection points',
      children: <PartnerCollectionPoints partner={partner} viewOnly={viewOnly} />,
    },
    {
      // OVO-113 — recurring orders. Admins get the flat-table CRUD + "Run
      // materialization now" trigger; partner viewers get a read-only table
      // fed by the JWT-scoped /partners/me/schedules endpoint.
      key: 'schedules',
      label: 'Recurring',
      children: viewOnly ? <PartnerSchedulesReadOnly /> : <PartnerSchedules partner={partner} />,
    },
    {
      // OVO-117 — support tickets. Admins get the partner-filtered admin slice;
      // viewers get the portal UI (JWT-scoped /partners/me endpoints — the
      // admin endpoints would 403 a viewer token).
      key: 'tickets',
      label: 'Tickets',
      children: viewOnly ? <PartnerTicketsPage embedded /> : <PartnerTicketsTab partner={partner} />,
    },
    {
      key: 'viewers',
      label: (
        <span>
          <UserOutlined /> {t('partners:viewers')}
        </span>
      ),
      children: (
        <Card
          title={t('partners:viewers')}
          extra={
            !viewOnly && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenViewerModal(null)}>
                Add Viewer
              </Button>
            )
          }
        >
          <Table
            dataSource={viewers}
            columns={viewerColumns}
            rowKey="id"
            loading={viewersLoading}
            locale={{ emptyText: 'No viewers created yet' }}
            size="small"
            pagination={false}
          />
        </Card>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      {contextHolder}
      <div className="PartnerDetailPage Content">
        <div className="ContentHeader">
          <BreadCrumb breadCrumbs={breadCrumbs} />
        </div>

        <div className="partner-detail-content">
          {!viewOnly && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/admin/partners')}
                className="back-button"
              >
                Back to Partners
              </Button>
              <Button icon={<EyeOutlined />} onClick={() => handleImpersonate(Number(id))} loading={impersonating}>
                {t('partners:viewAsViewer', 'View as viewer')}
              </Button>
            </div>
          )}

          <Tabs
            activeKey={activeTab}
            // OVO-002 — strip out viewer-hidden tabs (currently
            // "Collected Empties") for partner viewers.
            items={tabItems.filter((t) => !(viewOnly && t._hideForViewer))}
            onChange={handleTabChange}
          />
        </div>

        {!viewOnly && (
          <>
            {/* Edit Partner Modal */}
            <Modal
              title={t('partners:updatePartner')}
              open={editModalOpen}
              onCancel={() => setEditModalOpen(false)}
              footer={null}
              destroyOnClose
              width={600}
            >
              <Form form={partnerForm} layout="vertical" onFinish={handleSavePartner} requiredMark={false}>
                <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Partner name is required' }]}>
                  <Input placeholder="Partner name" />
                </Form.Item>
                <Form.Item label="Code" name="code">
                  <Input disabled />
                </Form.Item>
                <Form.Item
                  label="Contact Email"
                  name="contactEmail"
                  rules={[{ type: 'email', message: 'Invalid email' }]}
                >
                  <Input placeholder="ops@partner.com" />
                </Form.Item>
                <Form.Item label="Contact Phone" name="contactPhone">
                  <Input placeholder="+41..." />
                </Form.Item>
                <Form.Item label="Address" name="address">
                  <Input placeholder="Partner address" />
                </Form.Item>
                <Form.Item label="Notes" name="notes">
                  <TextArea rows={3} placeholder="Internal notes" />
                </Form.Item>
                <Row justify="end" gutter={8}>
                  <Col>
                    <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
                  </Col>
                  <Col>
                    <Button type="primary" htmlType="submit" loading={editSaving}>
                      Save
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Modal>

            {/* Item Type Modal */}
            <Modal
              title={editingItemType ? 'Edit Item Type' : 'Add Item Type'}
              open={itemTypeModalOpen}
              onCancel={() => setItemTypeModalOpen(false)}
              footer={null}
              destroyOnClose
              width={520}
            >
              <Form form={itemTypeForm} layout="vertical" onFinish={handleSaveItemType} requiredMark={false}>
                <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Item type name is required' }]}>
                  <Input placeholder="e.g. Blue crate, Bottle" />
                </Form.Item>
                <Form.Item label="Category" name="category">
                  <Select options={CATEGORY_OPTIONS} placeholder="Select category" />
                </Form.Item>
                <Form.Item label="Code Prefix" name="codePrefix">
                  <Input placeholder="e.g. BC-" />
                </Form.Item>
                <Form.Item label="Unit of Measure" name="unitOfMeasure">
                  <Input placeholder="e.g. pieces, kg" />
                </Form.Item>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name="requiresScan" valuePropName="checked">
                      <Checkbox>Requires Scan</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="requiresPhoto" valuePropName="checked">
                      <Checkbox>Requires Photo</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="requiresWeight" valuePropName="checked">
                      <Checkbox>Requires Weight</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="requiresFill" valuePropName="checked">
                      <Checkbox>Requires fill estimate (/10)</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
                <Row justify="end" gutter={8}>
                  <Col>
                    <Button onClick={() => setItemTypeModalOpen(false)}>Cancel</Button>
                  </Col>
                  <Col>
                    <Button type="primary" htmlType="submit" loading={itemTypeSaving}>
                      {editingItemType ? 'Update' : 'Create'}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Modal>

            {/* Partner Items Modal */}
            <PartnerItemsModal
              open={!!manageItemsType}
              onClose={() => {
                setManageItemsType(null);
                fetchItemTypes();
              }}
              itemType={manageItemsType}
            />

            {/* OVO-110 — partner-default column layout (admin) */}
            <PartnerColumnDefaultsModal
              open={columnDefaultsModalOpen}
              onClose={() => setColumnDefaultsModalOpen(false)}
              partnerId={Number(id)}
              initialValue={partner?.defaultColumns}
              onSaved={(updated) => setPartner(updated)}
            />

            {/* Webhook Config Modal */}
            <Modal
              title={t('partners:webhookConfig')}
              open={webhookModalOpen}
              onCancel={() => setWebhookModalOpen(false)}
              footer={null}
              destroyOnClose
              width={560}
            >
              <Form form={webhookForm} layout="vertical" onFinish={handleSaveWebhook} requiredMark={false}>
                <Form.Item label="Enabled" name="webhookEnabled" valuePropName="checked">
                  <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                </Form.Item>
                <Form.Item label="Payload Transformer" name="webhookTransformer">
                  <Select
                    allowClear
                    placeholder="Standard (default)"
                    options={[
                      { label: 'Standard', value: null },
                      { label: 'Legacy Webhook', value: 'legacy_webhook' },
                      { label: 'ET - Espace Terroir', value: 'et' },
                      { label: 'Bean - Odoo', value: 'bean' },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  label="Webhook URL"
                  name="webhookUrl"
                  rules={[{ type: 'url', message: 'Please enter a valid URL' }]}
                >
                  <Input placeholder="https://api.partner.com/webhook" />
                </Form.Item>
                <Form.Item
                  label="Custom Headers (JSON)"
                  name="webhookHeaders"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value || !value.trim()) return Promise.resolve();
                        try {
                          JSON.parse(value);
                          return Promise.resolve();
                        } catch {
                          return Promise.reject('Invalid JSON');
                        }
                      },
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={4}
                    placeholder={'{\n  "Authorization": "Bearer your-token"\n}'}
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                </Form.Item>
                <Form.Item
                  label="Max requests / second"
                  name="webhookRateLimitPerSecond"
                  tooltip="Throttle outbound webhooks to this partner. Leave empty to use the global default. Prevents a large dispatch (100+ orders) from bursting the endpoint into 429 errors."
                >
                  <InputNumber
                    min={0}
                    step={1}
                    placeholder={
                      webhookDefaultRate != null ? `Global default (${webhookDefaultRate}/s)` : 'Global default'
                    }
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item
                  label="Event types"
                  name="webhookEvents"
                  tooltip="Which events to send to this partner. Leave empty to send ALL events (default). Restrict it for partners that don't support certain event types."
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="All events (default)"
                    options={WEBHOOK_EVENT_OPTIONS}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Row justify="end" gutter={8}>
                  <Col>
                    <Button onClick={() => setWebhookModalOpen(false)}>Cancel</Button>
                  </Col>
                  <Col>
                    <Button type="primary" htmlType="submit" loading={webhookSaving}>
                      Save
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Modal>

            {/* Viewer Modal */}
            <Modal
              title={editingViewer ? 'Edit Viewer' : 'Add Viewer'}
              open={viewerModalOpen}
              onCancel={() => setViewerModalOpen(false)}
              footer={null}
              destroyOnClose
              width={520}
            >
              <Form form={viewerForm} layout="vertical" onFinish={handleSaveViewer} requiredMark={false}>
                <Form.Item
                  label="First Name"
                  name="giveName"
                  rules={[{ required: !editingViewer, message: 'First name is required' }]}
                >
                  <Input placeholder="First name" />
                </Form.Item>
                <Form.Item label="Last Name" name="familyName">
                  <Input placeholder="Last name" />
                </Form.Item>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: 'Email is required' },
                    { type: 'email', message: 'Invalid email' },
                  ]}
                >
                  <Input placeholder="viewer@partner.com" />
                </Form.Item>
                <Form.Item
                  label="Password"
                  name="password"
                  rules={
                    editingViewer
                      ? []
                      : [
                          { required: true, message: 'Password is required' },
                          { min: 6, message: 'Minimum 6 characters' },
                        ]
                  }
                >
                  <Input.Password placeholder={editingViewer ? 'Leave empty to keep current' : 'Password'} />
                </Form.Item>
                <Form.Item label={t('viewerLanguage')} name="language" initialValue="fr">
                  <Select>
                    <Select.Option value="fr">{t('viewerLanguageFr')}</Select.Option>
                    <Select.Option value="en">{t('viewerLanguageEn')}</Select.Option>
                  </Select>
                </Form.Item>
                <Row justify="end" gutter={8}>
                  <Col>
                    <Button onClick={() => setViewerModalOpen(false)}>Cancel</Button>
                  </Col>
                  <Col>
                    <Button type="primary" htmlType="submit" loading={viewerSaving}>
                      {editingViewer ? 'Update' : 'Create'}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Modal>
          </>
        )}
      </div>
    </Spin>
  );
};

export default PartnerDetailPage;
